import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Dialog, Table, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import translations from 'src/internationalisation/translation-map.json';
import { getPriceBookCompanyItems } from 'src/store/entities/price-book/price-book';
import { IPriceBookCompanyItem, IPriceBookItem } from 'src/common/interfaces/price-book.interfaces';
import { selectCostCodesIdToCodeMap } from 'src/store/entities/cost-codes/selectors';
import { CaslEnums } from 'src/enums/casl-enums';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { AccessSubjectTypes } from '@oliasoft/hierarchy-gui';
import { getPricebookAccessRightsList } from 'src/store/entities/access-control/access-control';
import { selectSubjectId } from 'src/store/entities/authorization/selectors';
import type { TRootState } from 'src/store/store-types';
import { dataHeaders, filterDataRows, headings } from './utils';

type TImportFromPriceBookModal = PropsFromRedux & {
  showImportFromPriceBookModalUpdated: (param: boolean) => void;
  parentId?: string;
  handleSubmit: (parentId: string | null, priceBookList: IPriceBookCompanyItem[], importWithLink: boolean) => void;
  importWithLink?: boolean;
};

const ImportFromPriceBookModal = ({
  priceBookItems,
  parentId,
  showImportFromPriceBookModalUpdated,
  isAdding,
  getPriceBookCompanyItems,
  costCodesIdToCodeMap,
  handleSubmit,
  importWithLink = false,
  getPricebookAccessRightsList,
  pricebookAccessRights,
  currentUserId,
}: TImportFromPriceBookModal) => {
  const { t } = useTranslation();

  useEffect(() => {
    getPriceBookCompanyItems();
    getPricebookAccessRightsList(AccessSubjectTypes.User);
  }, []);

  const onClose = () => showImportFromPriceBookModalUpdated(false);

  const [priceBookList, setPriceBookList] = useState<IPriceBookCompanyItem[]>([]);
  const [filters, setFilters] = useState({});

  const accessiblePriceBookItems = priceBookItems.filter((item) => {
    const accessConfig = pricebookAccessRights.find((a) => a.id === item.priceBookId);
    const hasAdminAccess = ability.can(ActionMap.MANAGE, CaslEnums.Access);
    const isPriceBookOwner = currentUserId === item.priceBookSubjectId;

    if (!accessConfig || hasAdminAccess || isPriceBookOwner) {
      return true;
    }
    return accessConfig.accessRights.some((rule) => rule.subjectid === currentUserId);
  });

  const filteredData = useMemo(
    () => filterDataRows(accessiblePriceBookItems, filters) ?? [],
    [accessiblePriceBookItems, filters],
  );

  const filteredIds = useMemo(() => new Set(filteredData.map((item) => item.priceBookItemId)), [filteredData]);

  const isAllFilteredSelected =
    filteredData.length > 0 &&
    filteredData.every((item) => priceBookList.some((selected) => selected.priceBookItemId === item.priceBookItemId));

  const selectAllCell = {
    key: 'selectAll',
    value: null,
    placeholder: '',
    onChange: () => {
      if (isAllFilteredSelected) {
        setPriceBookList((prev) => prev.filter((item) => !filteredIds.has(item.priceBookItemId)));
      } else {
        setPriceBookList((prev) => {
          const prevIds = new Set(prev.map((p) => p.priceBookItemId));
          const toAdd = filteredData.filter((item) => !prevIds.has(item.priceBookItemId));
          return [...prev, ...toAdd];
        });
      }
    },
    type: 'CheckBox',
    checked: isAllFilteredSelected,
  };

  const rows = filteredData?.map((priceBookItem: IPriceBookCompanyItem) => {
    const checked = priceBookList.some(
      (item: IPriceBookItem): boolean => item.priceBookItemId === priceBookItem.priceBookItemId,
    );
    return {
      cells: [
        {
          onChange: () => {
            if (checked) {
              setPriceBookList((prev) => prev.filter((item) => item.priceBookItemId !== priceBookItem.priceBookItemId));
            } else {
              setPriceBookList((prev) => [...prev, priceBookItem]);
            }
          },
          type: 'CheckBox',
          checked,
        },
        {
          name: 'name',
          value: priceBookItem.name,
        },
        {
          name: 'priceBookName',
          value: priceBookItem.priceBookName,
        },
        {
          name: 'costCodesId',
          value: costCodesIdToCodeMap.get(priceBookItem.costCodesId),
        },
        {
          name: 'vendor',
          value: priceBookItem.vendor,
        },
      ],
    };
  });

  const { dataHeaderCells, dataFilterCells } = dataHeaders(headings, filters, setFilters);
  dataFilterCells.splice(0, 1, selectAllCell);

  const tableConfig = {
    testId: 'cost-import-from-price-book-modal-table',
    columnWidths: ['5%', '40%', '28%', '27%'],
    headers: [
      {
        cells: dataHeaderCells,
      },
      {
        cells: dataFilterCells,
      },
    ],
    rows,
  };

  return (
    <Modal visible onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.priceBook_importFromPriceBook),
          width: '60vw',
          scroll: true,
          content: <Table table={tableConfig} />,
          footer: (
            <>
              <Button
                testId="cost-import-from-price-book-modal-button-confirm"
                label={t(translations.priceBook_importItems, {
                  count: priceBookList.length,
                })}
                colored
                disabled={!priceBookList.length || isAdding}
                onClick={() => handleSubmit(parentId ?? null, priceBookList, importWithLink)}
                loading={isAdding}
              />
              <Button
                testId="cost-import-from-price-book-modal-button-cancel"
                label={t(translations.cancel)}
                onClick={onClose}
              />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: TRootState) => {
  const { entities } = state;
  const { priceBookCompanyItems: priceBookItems } = entities.priceBook;
  const { isAdding } = entities.costSetup;

  return {
    priceBookItems,
    isAdding,
    costCodesIdToCodeMap: selectCostCodesIdToCodeMap(state),
    pricebookAccessRights: entities?.accessControl.pricebookAccessRights,
    currentUserId: selectSubjectId(entities),
  };
};

const mapDispatchToProps = {
  getPriceBookCompanyItems,
  getPricebookAccessRightsList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(ImportFromPriceBookModal), {
  style: { position: 'absolute', bottom: 0, zIndex: 1000 },
});

export { Container as ImportFromPriceBookModal };
